import { ReactNode } from 'react';
import {
  IActivityLog,
  IPublishingModel,
  allActivityLogTypes,
  EnsureExhaustiveActivityLogTypes,
  ISubmissionWorkflowState
} from '@publishing-engine/domain/models';
import {
  capitalize,
  getCheckListName,
  normalizeText,
  pluralizeString,
  splitPascalCase
} from 'src/utils/string';
import { SubmissionWithdrawal } from './SubmissionWithdrawal.js';
import { SubmissionUnsubmitted } from './SubmissionUnsubmitted.js';
import _ from 'lodash';
import { SubmissionRevision } from './SubmissionRevision.js';
import { getReviewerNameOrEmail } from 'src/utils/reviews.js';
import { ManuscriptIdDetails } from './ManuscriptIdDetails.js';
import { DecisionReverted } from './DecisionReverted.js';
import { SUBMISSION_STATUS_MAP } from '../../../constants.js';
import { Flex } from '@chakra-ui/react';
import { FormFieldDefinition } from '@publishing-engine/domain/types/article';
import { formatCamelCaseLabel } from 'src/pages/Organization/Workspace/Settings/Reports/index.js';

type IActivityLogText = {
  title: ReactNode;
  content?: ReactNode;
};

// Helper functions

/**
 * Builds a mapping from caseDataFields.main paths to display labels (params.labels.main)
 * from the submission fields API response.
 *
 * e.g. { "metadata.title": "Title", "fileSet.CoverLetter": "Cover Letter" }
 */
export const buildFieldLabelMap = (
  fields: Record<
    string,
    { formFieldDefinition: FormFieldDefinition; value: unknown }
  >
): Record<string, string> => {
  const labelMap: Record<string, string> = {};

  for (const field of Object.values(fields)) {
    const dataPath = field.formFieldDefinition.caseDataFields?.main;
    const label = field.formFieldDefinition.params.labels?.main;

    if (dataPath && label) {
      labelMap[dataPath] = label;
    }
  }

  return labelMap;
};

const getUpdatedFieldSummary = (
  changes: Record<string, any>[],
  fieldLabelMap?: Record<string, string>
): string => {
  // Revision Field Changed
  const uniqueChanges = new Set<string>();

  // Map the Text based on the field that was changed
  changes.forEach(change => {
    Object.entries(change)
      .filter(([key]) => key !== 'subtype')
      .forEach(([key]) => {
        // First try to resolve the label from the dynamic fieldLabelMap
        // by stripping the "submission." prefix and looking up the data path
        const dataPath = key.startsWith('submission.')
          ? key.slice('submission.'.length)
          : key;

        if (fieldLabelMap && fieldLabelMap[dataPath]) {
          uniqueChanges.add(fieldLabelMap[dataPath]);
          return;
        }

        // Fall back to the hardcoded changeMap for paths not found in field definitions
        const changeMap: Record<string, string> = {
          'submission.authorResponse': 'Author Response',
          'submission.correspondingAuthor': 'Corresponding Author',

          // Metadata
          'submission.metadata.title': 'Title',
          'submission.metadata.abstract': 'Abstract',
          'submission.metadata.keywords': 'Keywords',
          'submission.metadata.funding': 'Funding',
          'submission.metadata.noveltyStatement': 'Novelty Statement',
          'submission.metadata.conflictOfInterest': 'Conflict of Interest',
          'submission.metadata.dataAvailability': 'Data Availability',
          'submission.metadata.additionalFiles': 'Additional Files',
          'submission.metadata.clinicalTrials': 'Clinical Trials',
          'submission.metadata.ethicsStatement': 'Ethics Statement',
          'submission.metadata.englishSubtitle': 'English Subtitle',
          'submission.metadata.englishAbstract': 'English Abstract',
          'submission.metadata.englishKeywords': 'English Keywords',
          'submission.metadata.subtitle': 'Subtitle',
          'submission.metadata.englishTitle': 'English Title',
          'submission.metadata.articleSubtype': 'Article Subtype',
          'submission.metadata.aiTools': 'AI tools',
          'submission.metadata.preprint': 'Preprint',
          'submission.metadata.previouslySubmitted': 'Previously Submitted',
          'submission.metadata.ethicsApproval': 'Ethics Approval',
          'submission.metadata.ethicalApproval': 'Ethical Approval',
          'submission.metadata.sharedDataset': 'Shared Dataset',
          'submission.metadata.generatedDataset': 'Generated Dataset',
          'submission.metadata.utilizedDataset': 'Utilized Dataset',
          'submission.metadata.apcAgreement': 'APC Agreement',
          'submission.metadata.author': 'Author',
          'submission.preference.commentForEditor': 'Comment for Editor',

          // Files
          'submission.fileSet.SourceFiles': 'Source Files',
          'submission.fileSet.Manuscript': 'Manuscript',
          'submission.fileSet.TitlePage': 'TitlePage',
          'submission.fileSet.AnonymizedManuscript': 'Anonymised Manuscript',
          'submission.fileSet.Supplementary':
            'Supplementary File(s) (for review)',
          'submission.fileSet.AdditionalSupplementary':
            'Supplementary File(s) (NOT for review)',
          'submission.fileSet.CoverLetter': 'Cover Letter',
          'submission.authorResponseAttachments': 'Author Response Attachments',
          'submission.fileSet.CompleteDocumentForReview':
            'Complete Document for Review',
          'submission.fileSet.Figures': 'Figures',
          'submission.fileSet.Tables': 'Tables',
          'submission.fileSet.GraphicalAbstractImage': 'Graphical Abstract',

          // Publishing Preferences
          'submission.preference.acceleratedPublication':
            'Accelerated Publication',
          'submission.preference.ipemMembership': 'IPEM Membership',
          'submission.preference.immediatePublication': 'Immediate Publication',
          'submission.preference.subjectArea': 'Subject Area',
          'submission.preference.publishingModel': 'Publishing Model',
          'submission.preference.transparentPeerReview':
            'Transparent Peer Review',
          'submission.preference.doubleAnonymousPeerReview':
            'Double Anonymous Peer Review',
          'submission.preference.openAccessChoice': 'Open Access Choice',
          'submission.preference.standardReportingGuidelines':
            'Standard Reporting Guidelines',
          'submission.preference.numberOfTables': 'Number of Tables',
          'submission.preference.numberOfFigures': 'Number of Figures',
          'submission.preference.numberOfColorFigures':
            'Number of Color Figures',
          'submission.preference.numberOfWords': 'Number of Manuscript Words',
          'submission.preference.generalClassifications':
            'General Information & Classifications',
          'preference.coAuthorApproval': 'Co-Author Approval',

          // Licensing
          'submission.metadata.LicensesAndPermissionsReproducedContent':
            'Reproduced Content',
          'submission.fileSet.LicensesAndPermissionsFiles':
            'Permission Documents',

          //Data Availability
          'submission.preference.dataAvailability.status':
            'Data Availability Statement',
          'submission.preference.dataAvailabilityStatement':
            'Data Availability Statement',
          'submission.preference.dataAvailabilityEmbargoUrl': 'DOI',
          'submission.preference.dataAvailabilityEmbargoDate':
            'Embargo End Date',

          'submission.preference.focusIssue': 'Special Issue'
        };

        Object.keys(changeMap).forEach(changeKey => {
          if (key.includes(changeKey)) {
            uniqueChanges.add(changeMap[changeKey] || '');
          }
        });
      });
  });

  return Array.from(uniqueChanges).join(', ').replace(/,\s*$/, '');
};

const handleNoteAction = (action: string | undefined): IActivityLogText => {
  switch (action) {
    case 'created':
      return { title: 'New Note Added' };
    case 'deleted':
      return { title: 'Note Deleted' };
    case 'updated':
      return { title: 'Note Updated' };
    default:
      return { title: 'Note Action' };
  }
};

const handleExportSubmissionAction = (
  action: string | undefined
): IActivityLogText => {
  switch (action) {
    case 'complete':
      return { title: 'Submission Export Completed' };
    case 'failed':
      return { title: 'Submission Export Failed' };
    default:
      return { title: 'Submission Export Initiated' };
  }
};

const handleEditorialFormDataCreated = (
  change: Record<string, unknown> | undefined,
  isAdminChecklistDone: boolean | undefined,
  changeCount = 0
): IActivityLogText => {
  // Bulk checklist updates arrive as a single event with several changed
  // fields; show one summary entry rather than one entry per checkbox.
  if (isAdminChecklistDone || changeCount > 1) {
    return { title: 'Checklist Activity Completed' };
  }

  if (!change) return { title: 'EditorialFormDataCreated' };

  for (const key in change) {
    const fieldName = getCheckListName(key);
    const value = change[key];
    return { title: `${fieldName} ${value ? 'checked' : 'unchecked'}` };
  }
  return { title: 'EditorialFormDataCreated' };
};

const handleSubmissionEdited = (
  changes: Record<string, unknown>[] = []
): IActivityLogText => {
  const data: {
    key: string;
    value: unknown;
  } = {
    key: '',
    value: ''
  };

  for (const change of changes) {
    const k = Object.keys(change)[0];
    if (!k) continue;
    if (k === 'subtype') continue;

    /**
     * k is in the format
     * `submission.${'metadata' | 'preference' | 'fileSet'}.string?.string`
     *
     * e.g 'submission.metadata.authors.firstname', 'submission.metadata.abstract', etc
     */
    const [submission, changedPathGroup, changedPath, ...changedPathKeys] =
      k?.split('.') || [];
    data.key = changedPath!;

    if (changedPathKeys.length === 0) {
      data.value = _.get(
        change,
        `${submission}.${changedPathGroup}.${changedPath}`
      );
    } else {
      data.value = _.merge(
        data.value || {},
        changedPathKeys.reduce((acc, key) => {
          acc = _.set(
            acc,
            key,
            _.get(
              change,
              `${submission}.${changedPathGroup}.${changedPath}.${key}`
            )
          );

          return acc;
        }, {})
      );
    }
  }

  // Older or incomplete activity records can have no changed field path.
  // Keep rendering the activity instead of passing undefined to the label formatter.
  if (!data.key) return { title: 'Submission Edited' };

  switch (data.key) {
    case 'publishingModel':
      return {
        title: 'Publishing Preferences Edited',
        content:
          (data.value as IPublishingModel).model === 'openAccess'
            ? 'Publishing Model changed from Subscription to Open Access.'
            : 'Publishing Model changed from Open Access to Subscription.'
      };
    case 'transparentPeerReview':
      return {
        title: 'Peer Review Model Edited',
        content: data.value
          ? 'Submission will undergo through a Transparent Peer Review.'
          : "Submission won't undergo through a Transparent Peer Review."
      };
    case 'doubleAnonymousPeerReview':
      return {
        title: !data.value
          ? 'Peer Review Type Switched to Single Anonymous'
          : 'Peer Review Type Switched to Double Anonymous'
      };
    case 'LicensesAndPermissions':
      return {
        title: 'Licenses and Permissions Edited',
        content: `Updated answer: ${data.value}`
      };
    case 'LicensesAndPermissionsReproducedContent':
      return {
        title: 'Reproduced Content Edited'
      };
    case 'LicensesAndPermissionsFiles':
      return {
        title: 'Permission Documents Edited'
      };
    case 'fundingGeneric':
      return {
        title: 'Submission Funding Information Edited'
      };
    case 'coAuthorApproval':
      return {
        title: 'Submission Co-Author Approval Edited'
      };

    default:
      return { title: `Submission ${formatCamelCaseLabel(data.key)} Edited` };
  }
};
const handleVendorExportLogs = (
  status: string | undefined,
  configType: string | undefined
): IActivityLogText => {
  const isManual = configType === 'N/A';

  const action = (() => {
    switch (status) {
      case 'complete':
        return 'completed';
      case 'failed':
        return 'failed';
      default:
        return 'initiated';
    }
  })();

  const subject = isManual
    ? 'Manual download'
    : `Export for ${configType?.toLocaleUpperCase()}`;

  return {
    title: `${subject} ${action}`
  };
};
/**
 * Generic handler for licensing changes
 * Extracts and formats licensing fields that were changed
 */
const handleLicensingSubmitted = (
  changes: Record<string, unknown>[] = []
): IActivityLogText => {
  const licensingChanges: string[] = [];
  let fieldName = '';
  let title = 'Copyright Information';

  if (changes && Array.isArray(changes)) {
    changes.forEach(change => {
      if (typeof change === 'object' && change !== null) {
        const keys = Object.keys(change);
        if (keys.length > 0) {
          const key = keys[0];
          if (key && key.startsWith('submission.licensing.')) {
            const value = change[key];
            // Extract the field name from the key (e.g., "transformativeAgreementWaiverPercentage")
            fieldName = key.split('.').pop() || '';
            const displayName = normalizeText(fieldName);

            licensingChanges.push(
              `${displayName} ${value ? `set to ${value}` : 'removed'}`
            );
          }
        }
      }
    });
  }

  if (fieldName.match(/transformativeAgreement/i)) {
    title = 'Transformative Agreement Information';
  } else if (fieldName.match(/waiver/i)) {
    title = 'Waiver Information';
  }

  return {
    title: `${title} Edited`,
    content:
      licensingChanges.length > 0 ? (
        <Flex flexDirection={'column'}>
          {licensingChanges.map(c => (
            <span>{c}</span>
          ))}
        </Flex>
      ) : (
        'Licensing information was updated'
      )
  };
};

export const createActivityLogHandlers = (
  fieldLabelMap?: Record<string, string>
): EnsureExhaustiveActivityLogTypes<
  Record<allActivityLogTypes, (activityLog: IActivityLog) => IActivityLogText>
> => ({
  SubmissionCreated: () => ({ title: 'New Submission Added' }),
  SubmissionCloned: () => ({ title: 'Submission Cloned' }),

  SubmissionApprovedForPreProduction: () => ({
    title: 'Decision Made: Proceed to Provisional Acceptance'
  }),

  EditorialSubmissionDecision: (activityLog: IActivityLog) => {
    const [change] = activityLog.changes || [];
    let decision;
    switch (change?.subtype) {
      case 'reject':
        decision = 'Rejected';
        break;
      case 'withdraw':
        decision = 'Withdrawn';
        break;
      case 'approveForPeerReview':
        decision = 'Approved for Peer Review';
        break;
      default:
        decision = change?.subtype;
    }

    return { title: `Decision Made${decision ? `: ${decision}` : ''}` };
  },

  SubmissionAssigned: (activityLog: IActivityLog) => {
    const [change1, change2] = activityLog.changes || [];
    const subtype = change1?.subtype;
    const assignee = change2?.assignee;

    return {
      title: `Submission ${
        subtype === 'owner' ? 'Assigned To ' : 'Associated With '
      } ${assignee}`,
      content: ''
    };
  },

  SubmissionUnassigned: () => ({ title: 'Submission Unassigned' }),

  AuthorSubmissionEdited: (activityLog: IActivityLog) =>
    handleSubmissionEdited(activityLog.changes),

  SubmissionEdited: (activityLog: IActivityLog) =>
    handleSubmissionEdited(activityLog.changes),

  SubmissionNote: (activityLog: IActivityLog) =>
    handleNoteAction(activityLog.action),

  EditorialFormDataCreated: (activityLog: IActivityLog) => {
    const [change] = activityLog.changes || [];
    const isAdminChecklistDone = activityLog.isAdminChecklistDone || undefined;
    return handleEditorialFormDataCreated(
      change,
      isAdminChecklistDone,
      activityLog.changes?.length ?? 0
    );
  },

  // Peer Review
  ReviewRequested: (activityLog: IActivityLog) => {
    const [change] = activityLog.changes || [];
    const reviewerDisplay = getReviewerNameOrEmail({
      firstName: activityLog.reviewerFirstName,
      lastName: activityLog.reviewerLastName,
      email:
        typeof activityLog.reviewerEmail === 'string'
          ? activityLog.reviewerEmail
          : activityLog.reviewerEmail?.recipients?.to?.[0] || ''
    });

    const result: { title: string; content?: string } = {
      title: `Peer Review Invitation Sent`,
      content: `Reviewer Invited: ${reviewerDisplay}`
    };

    if (change?.subtype === 'automaticBoardMember') {
      result.title = `Peer Review Auto Accepted`;
      result.content = `Reviewer Accepted: ${reviewerDisplay}`;
    }

    return result;
  },
  DeclinedReviewInvitationResent: (activityLog: IActivityLog) => ({
    title: `Peer Review Invitation Resent`,
    content: `${getReviewerNameOrEmail({
      firstName: activityLog.reviewerFirstName,
      lastName: activityLog.reviewerLastName,
      email:
        typeof activityLog.reviewerEmail === 'string'
          ? activityLog.reviewerEmail
          : ''
    })} was reinvited and notified via email.`
  }),
  ExpiredReviewInvitationResent: (activityLog: IActivityLog) => ({
    title: `Peer Review Invitation Resent`,
    content: `${getReviewerNameOrEmail({
      firstName: activityLog.reviewerFirstName,
      lastName: activityLog.reviewerLastName,
      email:
        typeof activityLog.reviewerEmail === 'string'
          ? activityLog.reviewerEmail
          : ''
    })} was reinvited and notified via email.`
  }),
  WithdrawnReviewInvitationResent: (activityLog: IActivityLog) => ({
    title: `Peer Review Invitation Resent`,
    content: `${getReviewerNameOrEmail({
      firstName: activityLog.reviewerFirstName,
      lastName: activityLog.reviewerLastName,
      email:
        typeof activityLog.reviewerEmail === 'string'
          ? activityLog.reviewerEmail
          : ''
    })} was reinvited and notified via email.`
  }),
  PeerReviewDecision: (activityLog: IActivityLog) => {
    const [change] = activityLog.changes || [];
    const reviewerDisplay = getReviewerNameOrEmail({
      firstName: activityLog.reviewerFirstName,
      lastName: activityLog.reviewerLastName,
      email:
        typeof activityLog.reviewerEmail === 'string'
          ? activityLog.reviewerEmail
          : activityLog.reviewerEmail?.recipients?.to?.[0] || ''
    });
    let decision;
    switch (change?.subtype) {
      case 'acceptRequest':
        decision = 'Accepted';
        break;
      case 'declineRequest':
        decision = 'Declined';
        break;
      case 'autoAcceptRequest':
        decision = 'Auto Accepted';
        break;
      default:
        decision = change?.subtype;
    }

    const result: { title: string; content?: string } = {
      title: `Peer Review Invitation ${decision}`
    };

    if (change?.subtype === 'autoAcceptRequest') {
      result.content = `Reviewer Accepted: ${reviewerDisplay}`;
    }

    return result;
  },
  CoReviewDecision: (activityLog: IActivityLog) => {
    const [change] = activityLog.changes || [];
    let decision;
    switch (change?.subtype) {
      case 'acceptRequest':
        decision = 'Accepted';
        break;
      case 'declineRequest':
        decision = 'Declined';
        break;
      default:
        decision = change?.subtype;
    }

    return {
      title: `Co-Reviewer Invitation ${decision}`
    };
  },
  EditorialInvitedReviewInvitationWithdrawn: (activityLog: IActivityLog) => ({
    title: `${getReviewerNameOrEmail({
      firstName: activityLog.reviewerFirstName,
      lastName: activityLog.reviewerLastName,
      email:
        typeof activityLog.reviewerEmail === 'string'
          ? activityLog.reviewerEmail
          : ''
    })} Withdrawn from Peer Review`
  }),
  EditorialAcceptedReviewInvitationWithdrawn: (activityLog: IActivityLog) => ({
    title: `${getReviewerNameOrEmail({
      firstName: activityLog.reviewerFirstName,
      lastName: activityLog.reviewerLastName,
      email:
        typeof activityLog.reviewerEmail === 'string'
          ? activityLog.reviewerEmail
          : ''
    })} Withdrawn from Peer Review`
  }),
  EditorialAwaitingRevisionReviewInvitationWithdrawn: (
    activityLog: IActivityLog
  ) => ({
    title: `${getReviewerNameOrEmail({
      firstName: activityLog.reviewerFirstName,
      lastName: activityLog.reviewerLastName,
      email:
        typeof activityLog.reviewerEmail === 'string'
          ? activityLog.reviewerEmail
          : ''
    })} Withdrawn from Peer Review`
  }),
  EditorialRevisionInvitedReviewInvitationWithdrawn: (
    activityLog: IActivityLog
  ) => ({
    title: `${getReviewerNameOrEmail({
      firstName: activityLog.reviewerFirstName,
      lastName: activityLog.reviewerLastName,
      email:
        typeof activityLog.reviewerEmail === 'string'
          ? activityLog.reviewerEmail
          : ''
    })} Withdrawn from Peer Review`
  }),
  EditorialRevisionAssessmentReviewInvitationWithdrawn: (
    activityLog: IActivityLog
  ) => ({
    title: `${getReviewerNameOrEmail({
      firstName: activityLog.reviewerFirstName,
      lastName: activityLog.reviewerLastName,
      email:
        typeof activityLog.reviewerEmail === 'string'
          ? activityLog.reviewerEmail
          : ''
    })} Withdrawn from Peer Review`
  }),
  ReviewRequestEnqueued: (activityLog: IActivityLog) => ({
    title: `Peer Reviewers Added to Queue`,
    content: `Invited: ${activityLog.queue
      ?.map(r => getReviewerNameOrEmail(r))
      .join(', ')}`
  }),

  EnqueuedReviewerRemoved: (activityLog: IActivityLog) => ({
    title: `${getReviewerNameOrEmail({
      firstName: activityLog.reviewerFirstName,
      lastName: activityLog.reviewerLastName,
      email:
        typeof activityLog.reviewerEmail === 'string'
          ? activityLog.reviewerEmail
          : ''
    })} Removed from Queue`
  }),

  QueueToggled: (activityLog: IActivityLog) => ({
    title: `Automatic Queue ${activityLog.active ? 'Activated' : 'Deactivated'}`
  }),

  MinReviewCountSet: (activityLog: IActivityLog) => ({
    title: `Recommended Number of Reviews Edited`,
    content: `A new target of ${activityLog.count} Reviews has been set.`
  }),

  ReviewTargetMetEditorNotification: (activityLog: IActivityLog) => ({
    title: 'Review Target Met Editor Notification',
    content:
      typeof activityLog.count === 'number'
        ? `Review target reached (${activityLog.count} completed review(s)). Inviting editors were notified by email.`
        : 'Review target reached. Inviting editors were notified by email.'
  }),

  FirstAssessmentSubmitted: () => ({
    title: `Peer Review First Assessment Submitted`
  }),

  ReviewRated: (activityLog: IActivityLog) => ({
    title: `${getReviewerNameOrEmail({
      firstName: activityLog.reviewerFirstName,
      lastName: activityLog.reviewerLastName,
      email:
        typeof activityLog.reviewerEmail === 'string'
          ? activityLog.reviewerEmail
          : ''
    })}'s Review Rated`
  }),

  RescindedFirstAssessment: (activityLog: IActivityLog) => ({
    title: `${getReviewerNameOrEmail({
      firstName: activityLog.reviewerFirstName,
      lastName: activityLog.reviewerLastName,
      email:
        typeof activityLog.reviewerEmail === 'string'
          ? activityLog.reviewerEmail
          : ''
    })}'s Review Rescinded`
  }),

  PeerReviewModerated: (activityLog: IActivityLog) => ({
    title: `${getReviewerNameOrEmail({
      firstName: activityLog.reviewerFirstName,
      lastName: activityLog.reviewerLastName,
      email:
        typeof activityLog.reviewerEmail === 'string'
          ? activityLog.reviewerEmail
          : ''
    })}'s Author Feedback Moderated`
  }),

  // Revision
  RevisionRequested: (activityLog: IActivityLog) => ({
    title: `Decision Made: Revise${
      activityLog.revisionSeverity
        ? ` (${capitalize(activityLog.revisionSeverity)})`
        : ''
    }`,
    content: <SubmissionRevision changes={activityLog} />
  }),

  RevisionSubmitted: (activityLog: IActivityLog) => {
    // Revision Form Submission
    if (
      !activityLog.changes?.length ||
      activityLog.changes[0]?.subtype === 'form'
    ) {
      return { title: 'Revision Submitted' };
    }

    // TODO: Improve this logic here, we might not want to know every field submission
    const changesSummary = getUpdatedFieldSummary(
      activityLog.changes,
      fieldLabelMap
    );
    let content;
    if (changesSummary.length > 0) {
      content = 'Fields Changed: ' + changesSummary;
    }

    return {
      title: 'Revision Field Changed',
      content: content
    };
  },

  RevisionReviewRequested: (activityLog: IActivityLog) => ({
    title: `Revision Review Invitation Resent`,
    content: `${getReviewerNameOrEmail({
      firstName: activityLog.reviewerFirstName,
      lastName: activityLog.reviewerLastName,
      email:
        typeof activityLog.reviewerEmail === 'string'
          ? activityLog.reviewerEmail
          : ''
    })} was reinvited and notified via email.`
  }),

  RevisionAssessmentSubmitted: () => ({
    title: `Revision Review Submitted`
  }),

  SubmissionDeadlineExtensionRequested: () => ({
    title: `Submission Deadline Extension Requested`
  }),

  SubmissionDeadlineExtended: () => ({
    title: `Submission Deadline Extended`
  }),

  EditorialTransferOffered: (activityLog: IActivityLog) => {
    const targetJournals = activityLog.changes?.find(
      c => c['submission.targetJournals']
    )?.['submission.targetJournals'];

    return {
      title: `Decision Made: Transfer Offered`,
      content: Array.isArray(targetJournals)
        ? `Journals Offered to Transfer: ${targetJournals.join(', ')}`
        : ''
    };
  },

  TransferOfferAccepted: (activityLog: IActivityLog) => ({
    title: `Transfer Offer Accepted`,
    content: (
      <ManuscriptIdDetails
        changes={activityLog.changes}
        type={activityLog.type as 'TransferOfferAccepted'}
      />
    )
  }),

  TransferDeclined: () => ({
    title: `Transfer Offer Rejected`
  }),

  AuthorSubmissionWithdrawalRequested: (activityLog: IActivityLog) => ({
    title: 'Decision Made: Request Withdrawal',
    content: (
      <SubmissionWithdrawal
        changes={activityLog.changes}
        type={activityLog.type as 'AuthorSubmissionWithdrawalRequested'}
      />
    )
  }),

  AuthorSubmissionWithdrawalRequestRevoked: () => ({
    title: 'Decision Made: Withdrawal Request Cancelled'
  }),

  EditorialSubmissionWithdrawn: (activityLog: IActivityLog) => ({
    title: 'Decision Made: Withdraw',
    content: (
      <SubmissionWithdrawal
        changes={activityLog.changes}
        type={activityLog.type as 'EditorialSubmissionWithdrawn'}
      />
    )
  }),

  EditorialSubmissionWithdrawalAccepted: (activityLog: IActivityLog) => ({
    title: "Decision Made: Withdraw at Author's Request",
    content: (
      <SubmissionWithdrawal
        changes={activityLog.changes}
        type={activityLog.type as 'EditorialSubmissionWithdrawalAccepted'}
      />
    )
  }),

  // Flags and Tags
  SubmissionTagsReplaced: (activityLog: IActivityLog) => {
    const hasAddedTags = (activityLog.tagAddList?.length ?? 0) > 0;
    const hasDeletedTags = (activityLog.tagDeleteList?.length ?? 0) > 0;

    const addedTags = hasAddedTags
      ? `Added: ${activityLog.tagAddList?.join(', ')}`
      : '';
    const deletedTags = hasDeletedTags
      ? `Deleted: ${activityLog.tagDeleteList?.join(', ')}`
      : '';
    const comma = addedTags && deletedTags ? ', ' : '';

    return {
      title: 'Submission Tags Edited',
      content: `${addedTags}${comma}${deletedTags}`
    };
  },

  EditorialSubmissionUnsubmitted: (activityLog: IActivityLog) => ({
    title: 'Decision Made: Unsubmit',
    content: <SubmissionUnsubmitted changes={activityLog.changes} />
  }),

  AuthorSubmissionResubmitted: () => ({
    title: 'Revision Submitted'
  }),

  AuthorChangeRequestCompleted: (activityLog: IActivityLog) => {
    if (
      !activityLog.changes?.length ||
      activityLog.changes[0]?.subtype === 'form'
    ) {
      return { title: 'Fields Updated' };
    }

    const changesSummary = getUpdatedFieldSummary(
      activityLog.changes,
      fieldLabelMap
    );
    let content;
    if (changesSummary.length > 0) {
      content = 'Fields Updated: ' + changesSummary;
    }

    return {
      title: 'Fields Updated',
      content
    };
  },

  EditorialChangeRequestSubmitted: (activityLog: IActivityLog) => {
    const transformFields = (fields: string[]) => {
      return fields.map(splitPascalCase).join(', ');
    };

    let title: string = 'Change Request Logged';
    if (
      !activityLog.changes?.length ||
      activityLog.changes[0]?.subtype === 'form'
    ) {
      title = 'Change Request Submitted to Author';
    }

    return {
      title,
      content:
        activityLog.fields && Array.isArray(activityLog.fields)
          ? `Fields requested: ${transformFields(activityLog.fields)}`
          : undefined
    };
  },

  AcceptedForFormalAcceptance: () => ({
    title: 'Decision Made: Proceed to Formal Acceptance',
    content: ''
  }),

  SubmissionDeadlinePassed: () => ({
    title: 'Submission Deadline Passed'
  }),

  LicensingSubmitted: (activityLog: IActivityLog) =>
    handleLicensingSubmitted(activityLog.changes),

  EditorialSubmissionRejected: () => ({
    title: 'Decision Made: Reject'
  }),

  ReviewDeadlinePassed: () => ({
    title: 'Review Deadline Passed'
  }),

  ReviewDeadlineExtensionRequested: () => ({
    title: 'Review Deadline Extension Requested'
  }),

  ReviewDeadlineExtended: () => ({
    title: 'Review Deadline Extended'
  }),

  ReviewInRevisionRequested: (activityLog: IActivityLog) => {
    const [change] = activityLog.changes || [];
    const reviewerDisplay = getReviewerNameOrEmail({
      firstName: activityLog.reviewerFirstName,
      lastName: activityLog.reviewerLastName,
      email:
        typeof activityLog.reviewerEmail === 'string'
          ? activityLog.reviewerEmail
          : activityLog.reviewerEmail?.recipients?.to?.[0] || ''
    });

    const result: { title: string; content?: string } = {
      title: `Review In Revision Requested`,
      content: `Reviewer Invited: ${reviewerDisplay}`
    };

    if (change?.subtype === 'automaticBoardMember') {
      result.title = `Review In Revision Requested Auto Accepted`;
      result.content = `Reviewer Accepted: ${reviewerDisplay}`;
    }

    return result;
  },

  ReviewRemoved: () => ({
    title: 'Review Removed'
  }),

  IntegrationResult: () => ({
    title: 'Integration Result'
  }),

  AuthorCopyrightSubmitted: () => ({
    title: 'Copyright Form Submitted By Author'
  }),

  AdhocEmailSent: () => ({
    title: 'Ad hoc Email Sent'
  }),

  EditorialCopyrightRequested: () => ({
    title: 'Copyright Form Requested By Editor'
  }),

  EditorialCopyrightUploaded: () => ({
    title: 'Copyright Form Uploaded By Editor'
  }),

  EditorialICMJEUploaded: () => ({
    title: 'Forms and Disclosures Uploaded By Editor'
  }),

  EditorialICMJERequested: () => ({
    title: 'Forms and Disclosures Requested By Editor'
  }),

  EarlyAssessmentRequested: () => ({
    title: 'Early Assessment Requested'
  }),

  EarlyAssessmentRequestDecision: () => ({
    title: 'Early Assessment Request Decision'
  }),

  EarlyAssessmentSubmitted: () => ({
    title: 'Early Assessment Submitted'
  }),

  AdjudicationRequested: () => ({
    title: 'Adjudication Requested'
  }),

  AdjudicationRequestDecision: () => ({
    title: 'Adjudication Request Decision'
  }),

  AdjudicationSubmitted: () => ({
    title: 'Adjudication Submitted'
  }),

  EditorialImmediateRevisionRequested: (activityLog: IActivityLog) => ({
    title: 'Decision Made: Immediate Revision',
    content: <SubmissionRevision changes={activityLog} />
  }),

  CoReviewerInvited: () => ({
    title: 'Co-Reviewer Invited'
  }),

  ReviewReminderTriggered: () => ({
    title: 'Review reminder triggered'
  }),

  SubmissionReminderTriggered: () => ({
    title: 'Submission reminder triggered'
  }),

  AdjudicationReminderTriggered: () => ({
    title: 'Adjudication reminder triggered'
  }),

  EarlyAssessmentReminderTriggered: () => ({
    title: 'Early Assessment reminder triggered'
  }),

  SubmissionDraftCreated: () => ({
    title: 'Submission draft created'
  }),
  SubmissionAccessed: () => ({
    title: 'Submission accessed'
  }),
  SubmissionDeadlineExtensionDenied: () => ({
    title: `Submission Deadline Extension Request Declined`
  }),
  ReviewDeadlineExtensionDenied: () => ({
    title: `Review Deadline Extension Request Declined`
  }),
  ExportPreprodSubmission: (activityLog: IActivityLog) =>
    handleExportSubmissionAction(activityLog.action),

  PeerReviewInterrupted: (activityLog: IActivityLog) => {
    const closedReviewCount = activityLog.closedReviews?.length || 0;
    const reviewerNames = activityLog.closedReviews
      ?.map(
        r => `${r.caseData.reviewer.firstName} ${r.caseData.reviewer.lastName}`
      )
      .join(', ');

    return {
      title: 'Peer Review Round Interrupted',
      content: `The following ${pluralizeString(
        'reviewer',
        closedReviewCount
      )} were removed due to incomplete ${pluralizeString(
        'review',
        closedReviewCount
      )} when this round was terminated: ${reviewerNames}`
    };
  },
  ArticleTypeChanged: ({ eventPayload }: IActivityLog) => {
    const { oldName, newName } = eventPayload || {};

    return {
      title: 'Submission Manuscript Type Edited',
      content: `Original: ${oldName} -> New: ${newName}`
    };
  },
  AuthorCopyrightUploaded: () => ({
    title: 'Copyright Form Uploaded By Author'
  }),
  AuthorICMJEUploaded: () => ({
    title: 'Forms and Disclosures Uploaded By Author'
  }),
  DecisionReverted: ({ eventPayload }: IActivityLog) => {
    const {
      previousPhase = '',
      currentPhase = '',
      reason = ''
    } = eventPayload || {};
    const previous =
      SUBMISSION_STATUS_MAP[previousPhase as ISubmissionWorkflowState]?.label ||
      previousPhase;
    const current =
      SUBMISSION_STATUS_MAP[currentPhase as ISubmissionWorkflowState]?.label ||
      currentPhase;

    return {
      title: `Decision Reversal: ${previous} -> ${current}`,
      content: (
        <DecisionReverted
          data={{
            reason,
            previousPhase: previous,
            currentPhase: current
          }}
        />
      )
    };
  },
  AuthorCopyrightReset: () => ({
    title: 'Copyright Form Deleted',
    content:
      'The existing copyright form sent by the authors was deleted due to an update to this submission’s publishing model. The authors will need to provide a new form.'
  }),
  SubmissionAssignedCOC: (activityLog: IActivityLog) => ({
    title: `Submission ${activityLog.isSystem ? `Auto` : ''} Assigned ${
      activityLog.role ? `- ${activityLog.role}` : 'COC'
    } `,
    content: `${activityLog.role} Assigned :  ${activityLog.assignee?.firstName} ${activityLog.assignee?.lastName} `
  }),
  COCEditorAssigned: (activityLog: IActivityLog) => ({
    title: `Submission Assigned ${
      activityLog.role ? `- ${activityLog.role}` : 'Editor'
    }`,
    content: `${activityLog.role} Assigned :  ${activityLog.assignee?.firstName} ${activityLog.assignee?.lastName} `
  }),
  COCRecommendationSubmitted: (activityLog: IActivityLog) => ({
    title: ` ${
      !activityLog.isOwner && activityLog.autoAssign
        ? 'Decision'
        : 'Recommendation'
    } Submitted - ${activityLog.role ? `${activityLog.role}` : 'COC'} `
  }),
  COCRoleUnassigned: (activityLog: IActivityLog) => ({
    title: `Submission Unassigned ${
      activityLog.role ? `- ${activityLog.role}` : 'COC'
    } `,
    content: `${activityLog.role} Unassigned :  ${activityLog.assignee?.firstName} ${activityLog.assignee?.lastName} `
  }),
  VendorExport: (activityLog: IActivityLog) =>
    handleVendorExportLogs(activityLog?.status, activityLog?.exportConfigType),
  COCRecommendationModerated: () => ({
    title: 'Editor Recommendation Moderated'
  }),
  ContactJournal: () => ({
    title: 'Submission issue or query sent to journal'
  }),
  NotifyAuthorOnManuscriptUpdate: () => ({
    title: 'Notify Author on Manuscript Update'
  })
});
